import React, { useState, useCallback, useEffect } from 'react';
import { View, Text, StyleSheet, ScrollView, TouchableOpacity } from 'react-native';
import { ActivitiesDefStorage, ActivitiesDefDto, ActivitiesDomainDto, ActivitiesItemDto } from '../../storage/ActivitiesDefStorage';
import { useProfileStore } from '../../contexts/StoreContext';
import { formatPhysicalAge } from '../../utils/ageFormatting';
import { appConfig } from '../../config/configManager';

type ActivityTab = 'AllActivities' | 'Communication' | 'MotorSkills' | 'Cognitive' | 'SocialEmotional';
type ActViewMode = 'Current' | 'More' | 'All';

interface TabInfo {
  key: string;
  name: string;
  icon: string;
  description: string;
}

const ACTIVITY_TABS: TabInfo[] = [
  { key: 'Communication',   name: 'Communication',    icon: '💬', description: 'Language, speech and communication activities' },
  { key: 'MotorSkills',     name: 'Motor Skills',     icon: '🏃', description: 'Gross and fine motor development activities' },
  { key: 'Cognitive',       name: 'Cognitive',        icon: '🧩', description: 'Thinking, problem-solving and learning activities' },
  { key: 'SocialEmotional', name: 'Social Emotional', icon: '❤️', description: 'Social interaction and emotional development activities' },
];

interface Activity {
  id: string;
  title: string;
  description: string;
  ageRange: string;
  category: 'motor' | 'cognitive' | 'social' | 'creative' | 'language';
  duration: string;
  materials: string[];
  instructions: string[];
  benefits: string[];
  icon: string;
}

export const ActivitiesView: React.FC = () => {
  const [selectedTabIndex, setSelectedTabIndex] = useState(0); // Default to All Activities
  const [selectedCategory, setSelectedCategory] = useState<string>('all');
  const profileStore = useProfileStore();
  const { getActiveProfile } = profileStore();
  const activeProfile = getActiveProfile();

  const [actViewMode, setActViewMode] = useState<ActViewMode>('Current');

  // ─── ActivitiesDef storage (loaded via global __childNavStorage) ──────────
  const [activitiesDef, setActivitiesDef] = useState<ActivitiesDefDto | null>(null);

  const loadActivitiesDef = useCallback(async () => {
    const storage = (global as any).__childNavStorage ?? (globalThis as any).__childNavStorage ?? null;
    if (!storage) return;
    const store = new ActivitiesDefStorage(storage);
    const def = await store.getActivitiesDef();
    if (def) {
      setActivitiesDef(def);
      setSelectedTabIndex(0);
    }
  }, []);

  useEffect(() => {
    loadActivitiesDef();
  }, [loadActivitiesDef]);

  // ─── Dynamic tabs derived from stored def ────────────────────────────────
  const DOMAIN_ICONS: Record<string, string> = {
    communication: '💬', language: '💬', speech: '💬',
    motor: '🏃', 'motor skills': '🏃', gross: '🏃', fine: '✋',
    cognitive: '🧩', thinking: '🧩', problem: '🧩',
    social: '❤️', 'social emotional': '❤️', emotional: '❤️',
    creative: '🎨', art: '🎨',
    sensory: '👁️', reading: '📚',
  };

  const getDomainIcon = (name: string): string => {
    const key = name.toLowerCase();
    for (const [k, icon] of Object.entries(DOMAIN_ICONS)) {
      if (key.includes(k)) return icon;
    }
    return '📋';
  };

  const PREMIUM_ONLY_DOMAINS = ['cdc extra'];
  const currentTabs: TabInfo[] = activitiesDef
    ? [
        ...activitiesDef.domains
          .filter(d => {
            const isPremiumOnly = PREMIUM_ONLY_DOMAINS.includes(d.name?.toLowerCase().trim());
            return !isPremiumOnly || appConfig.isPremium();
          })
          .map((d): TabInfo => ({
            key: `domain_${d.name}`,
            name: d.name,
            icon: getDomainIcon(d.name),
            description: d.desc || `${d.name} activities`,
          })),
      ]
    : ACTIVITY_TABS;

  const selectedTab = currentTabs[selectedTabIndex] ?? currentTabs[0];

  // ─── Age-based group filtering helpers ─────────────────────────────────
  const getProfileAgeInDays = (dob: string): number => {
    if (!dob) return 0;
    const birth = new Date(dob);
    const now = new Date();
    return Math.floor((now.getTime() - birth.getTime()) / (1000 * 60 * 60 * 24));
  };

  const partitionGroups = (groups: import('../../storage/ActivitiesDefStorage').ActivitiesGroupDto[]) => {
    const regular = groups
      .filter(g => parseInt(g.days, 10) !== -1)
      .sort((a, b) => parseInt(a.days, 10) - parseInt(b.days, 10));
    const always = groups.filter(g => parseInt(g.days, 10) === -1);
    return { regular, always };
  };

  const selectGroupsForMode = (
    groups: import('../../storage/ActivitiesDefStorage').ActivitiesGroupDto[],
    profileAgeDays: number,
    mode: ActViewMode,
  ): import('../../storage/ActivitiesDefStorage').ActivitiesGroupDto[] => {
    const { regular, always } = partitionGroups(groups);
    if (mode === 'All') return [...regular, ...always];
    let currentIdx = regular.length > 0 ? 0 : -1;
    for (let i = 0; i < regular.length; i++) {
      const groupDays = parseInt(regular[i].days, 10);
      const nextDays = i + 1 < regular.length ? parseInt(regular[i + 1].days, 10) : Number.MAX_SAFE_INTEGER;
      if (profileAgeDays >= groupDays && profileAgeDays < nextDays) { currentIdx = i; break; }
    }
    if (mode === 'Current') {
      const selected = currentIdx >= 0 ? [regular[currentIdx]] : [];
      return [...selected, ...always];
    }
    const start = Math.max(0, currentIdx - 1);
    const end = Math.min(regular.length - 1, currentIdx + 1);
    const selected = currentIdx >= 0 ? regular.slice(start, end + 1) : [];
    return [...selected, ...always];
  };

  const handlePrevTab = () => {
    if (selectedTabIndex > 0) setSelectedTabIndex(selectedTabIndex - 1);
  };

  const handleNextTab = () => {
    if (selectedTabIndex < currentTabs.length - 1) setSelectedTabIndex(selectedTabIndex + 1);
  };

  // Sample activities data - in a real app, this would come from a database
  const activities: Activity[] = [
    {
      id: 'motor_1',
      title: 'Stacking Blocks',
      description: 'Build towers and practice hand-eye coordination',
      ageRange: '12-24 months',
      category: 'motor',
      duration: '10-15 minutes',
      materials: ['Soft blocks', 'Safe surface'],
      instructions: [
        'Start with 2-3 large, soft blocks',
        'Show your child how to stack one on top of another',
        'Encourage them to try stacking',
        'Celebrate when they succeed!',
        'Gradually add more blocks as they improve'
      ],
      benefits: [
        'Develops fine motor skills',
        'Improves hand-eye coordination',
        'Teaches cause and effect',
        'Builds concentration'
      ],
      icon: '🧱'
    },
    {
      id: 'language_1',
      title: 'Story Time',
      description: 'Interactive reading to develop language skills',
      ageRange: '6-36 months',
      category: 'language',
      duration: '5-20 minutes',
      materials: ['Picture books', 'Comfortable seating'],
      instructions: [
        'Choose age-appropriate books with colorful pictures',
        'Point to pictures while reading',
        'Use different voices for characters',
        'Ask simple questions about the story',
        'Let your child turn the pages'
      ],
      benefits: [
        'Expands vocabulary',
        'Develops listening skills',
        'Encourages imagination',
        'Builds bonding time'
      ],
      icon: '📚'
    },
    {
      id: 'cognitive_1',
      title: 'Shape Sorting',
      description: 'Learn shapes, colors, and problem-solving',
      ageRange: '12-30 months',
      category: 'cognitive',
      duration: '10-20 minutes',
      materials: ['Shape sorter toy', 'Various shaped objects'],
      instructions: [
        'Start with one shape at a time',
        'Name the shape and color',
        'Guide their hand to the correct hole',
        'Let them try independently',
        'Praise their efforts and successes'
      ],
      benefits: [
        'Teaches shapes and colors',
        'Develops problem-solving skills',
        'Improves spatial awareness',
        'Enhances cognitive development'
      ],
      icon: '🔷'
    },
    {
      id: 'social_1',
      title: 'Peek-a-Boo',
      description: 'Classic game for social interaction and bonding',
      ageRange: '3-18 months',
      category: 'social',
      duration: '5-10 minutes',
      materials: ['Your hands or a soft cloth'],
      instructions: [
        'Cover your face with your hands',
        'Say "Where did I go?"',
        'Reveal your face and say "Peek-a-boo!"',
        'Watch for your child\'s reaction',
        'Try letting them cover their own face'
      ],
      benefits: [
        'Develops social skills',
        'Teaches object permanence',
        'Builds trust and security',
        'Encourages laughter and joy'
      ],
      icon: '👀'
    },
    {
      id: 'creative_1',
      title: 'Finger Painting',
      description: 'Explore colors and textures through art',
      ageRange: '12-36 months',
      category: 'creative',
      duration: '15-30 minutes',
      materials: ['Non-toxic finger paints', 'Large paper', 'Aprons', 'Wet wipes'],
      instructions: [
        'Set up a protected area',
        'Put on aprons or old clothes',
        'Start with one color',
        'Show how to dip fingers in paint',
        'Let them explore freely on paper'
      ],
      benefits: [
        'Encourages creativity',
        'Develops sensory awareness',
        'Improves fine motor skills',
        'Provides emotional expression'
      ],
      icon: '🎨'
    },
    {
      id: 'motor_2',
      title: 'Ball Rolling',
      description: 'Practice coordination and cause-and-effect',
      ageRange: '6-24 months',
      category: 'motor',
      duration: '10-15 minutes',
      materials: ['Soft balls of various sizes'],
      instructions: [
        'Sit on the floor facing your child',
        'Roll a ball gently toward them',
        'Encourage them to push it back',
        'Try different sized balls',
        'Celebrate their attempts'
      ],
      benefits: [
        'Develops gross motor skills',
        'Improves coordination',
        'Teaches turn-taking',
        'Builds social interaction'
      ],
      icon: '⚽'
    }
  ];

  const categories = [
    { key: 'all', label: 'All Activities', icon: '🌟' },
    { key: 'motor', label: 'Motor Skills', icon: '🏃' },
    { key: 'cognitive', label: 'Cognitive', icon: '🧠' },
    { key: 'social', label: 'Social', icon: '👥' },
    { key: 'creative', label: 'Creative', icon: '🎨' },
    { key: 'language', label: 'Language', icon: '💬' }
  ];

  const filteredActivities = selectedCategory === 'all'
    ? activities
    : activities.filter(activity => activity.category === selectedCategory);

  const renderActivityCard = (activity: Activity) => (
    <View key={activity.id} style={styles.activityCard}>
      <View style={styles.activityHeader}>
        <Text style={styles.activityIcon}>{activity.icon}</Text>
        <View style={styles.activityTitleContainer}>
          <Text style={styles.activityTitle}>{activity.title}</Text>
          <Text style={styles.activityAge}>{activity.ageRange}</Text>
        </View>
        <View style={styles.durationBadge}>
          <Text style={styles.durationText}>{activity.duration}</Text>
        </View>
      </View>

      <Text style={styles.activityDescription}>{activity.description}</Text>

      <View style={styles.materialsSection}>
        <Text style={styles.sectionLabel}>Materials Needed:</Text>
        <View style={styles.materialsList}>
          {activity.materials.map((material, index) => (
            <Text key={index} style={styles.materialItem}>• {material}</Text>
          ))}
        </View>
      </View>

      <View style={styles.instructionsSection}>
        <Text style={styles.sectionLabel}>Instructions:</Text>
        {activity.instructions.map((instruction, index) => (
          <Text key={index} style={styles.instructionItem}>
            {index + 1}. {instruction}
          </Text>
        ))}
      </View>

      <View style={styles.benefitsSection}>
        <Text style={styles.sectionLabel}>Benefits:</Text>
        <View style={styles.benefitsList}>
          {activity.benefits.map((benefit, index) => (
            <View key={index} style={styles.benefitItem}>
              <Text style={styles.benefitIcon}>✓</Text>
              <Text style={styles.benefitText}>{benefit}</Text>
            </View>
          ))}
        </View>
      </View>
    </View>
  );

  const renderCurrentContent = () => (
    <>
      {/* Category filter row */}
      <ScrollView horizontal showsHorizontalScrollIndicator={false} style={styles.categoryTabs}>
        {categories.map((category) => (
          <TouchableOpacity
            key={category.key}
            style={[
              styles.categoryTab,
              selectedCategory === category.key && { backgroundColor: appConfig.getNavButtonColor() }
            ]}
            onPress={() => setSelectedCategory(category.key)}
          >
            <Text style={styles.categoryTabIcon}>{category.icon}</Text>
            <Text style={[
              styles.categoryTabText,
              selectedCategory === category.key && styles.activeCategoryTabText
            ]}>
              {category.label}
            </Text>
          </TouchableOpacity>
        ))}
      </ScrollView>

      <View style={styles.activitiesGrid}>
        {filteredActivities.map(renderActivityCard)}
        <View style={styles.footer}>
          <Text style={styles.footerText}>
            💡 Tip: Always supervise your child during activities and adjust based on their individual development level.
          </Text>
        </View>
      </View>
    </>
  );

  const renderPlaceholderContent = (tab: TabInfo) => (
    <View style={styles.emptyContainer}>
      <Text style={styles.emptyText}>
        {tab.icon}  {tab.name} activities will be loaded here
      </Text>
    </View>
  );

  const renderDefItemCard = (item: ActivitiesItemDto, idx: number) => (
    <View key={idx} style={styles.activityCard}>
      <View style={styles.activityHeader}>
        <Text style={styles.activityIcon}>{getDomainIcon(item.devDomain)}</Text>
        <View style={styles.activityTitleContainer}>
          <Text style={styles.activityTitle}>{item.name}</Text>
          {!!item.days && (
            <Text style={styles.activityAge}>From {item.days} days</Text>
          )}
        </View>
        {!!item.devDomain && (
          <View style={styles.durationBadge}>
            <Text style={styles.durationText}>{item.devDomain}</Text>
          </View>
        )}
      </View>
      <Text style={styles.activityDescription}>{item.desc}</Text>
    </View>
  );

  const AGE_SELECTOR_EXEMPT_DOMAINS = ['serve and return'];

  const renderDomainContent = (domain: ActivitiesDomainDto) => {
    const showSelector = !AGE_SELECTOR_EXEMPT_DOMAINS.includes(domain.name?.toLowerCase().trim());
    const profileAgeDays = activeProfile?.dob ? getProfileAgeInDays(activeProfile.dob) : 0;
    const visibleGroups = showSelector
      ? selectGroupsForMode(domain.groups, profileAgeDays, actViewMode)
      : [...domain.groups].sort((a, b) => Number(a.days ?? 0) - Number(b.days ?? 0));
    return (
    <View style={styles.activitiesGrid}>
      {!!domain.desc && (
        <View style={[styles.activityCard, { backgroundColor: appConfig.getNavButtonColor(), marginBottom: 8 }]}>
          <Text style={styles.domainDescriptionCard}>{domain.desc}</Text>
        </View>
      )}
      {showSelector && (
      <View style={styles.actViewSelector}>
        {(['Current', 'More', 'All'] as ActViewMode[]).map(mode => (
          <TouchableOpacity
            key={mode}
            style={[styles.actViewBtn, actViewMode === mode && { backgroundColor: appConfig.getNavButtonColor() }]}
            onPress={() => setActViewMode(mode)}
          >
            <Text style={[styles.actViewBtnText, actViewMode === mode && styles.actViewBtnTextActive]}>{mode}</Text>
          </TouchableOpacity>
        ))}
      </View>
      )}
      {visibleGroups.map((group, gi) => (
        <View key={gi} style={styles.groupCard}>
          {!!group.name && (
            <Text style={styles.groupCardTitle}>{group.name}</Text>
          )}
          {!!group.desc && (
            <Text style={styles.groupCardDesc}>{group.desc}</Text>
          )}
          {group.items.map((item, ii) => (
            <View key={ii} style={styles.groupItemRow}>
              <Text style={styles.groupItemBullet}>•</Text>
              <View style={styles.groupItemContent}>
                <Text style={styles.groupItemName}>{item.name}</Text>
                {!!item.desc && (
                  <Text style={styles.groupItemDesc}>{item.desc}</Text>
                )}
              </View>
            </View>
          ))}
        </View>
      ))}
      <View style={styles.footer}>
        <Text style={styles.footerText}>
          💡 Tip: Always supervise your child during activities and adjust based on their individual development level.
        </Text>
        {!!domain.footer && (
          <Text style={styles.sourceCredit}>{domain.footer}</Text>
        )}
      </View>
    </View>
    );
  };

  const renderAllDefContent = () => {
    if (!activitiesDef) return renderCurrentContent();
    return (
      <View style={styles.activitiesGrid}>
        {activitiesDef.domains.map((domain, di) => (
          <View key={di}>
            <View style={[styles.activityCard, { backgroundColor: '#f0f9ff', marginBottom: 4 }]}>
              <Text style={[styles.sectionLabel, { fontSize: 18 }]}>{getDomainIcon(domain.name)}  {domain.name}</Text>
              {!!domain.desc && <Text style={styles.activityDescription}>{domain.desc}</Text>}
            </View>
            {domain.groups.flatMap(g => g.items).slice(0, 3).map((item, ii) => renderDefItemCard(item, ii))}
          </View>
        ))}
        <View style={styles.footer}>
          <Text style={styles.footerText}>
            💡 Tip: Always supervise your child during activities and adjust based on their individual development level.
          </Text>
        </View>
      </View>
    );
  };

  const renderContent = () => {
    if (activitiesDef) {
      const domainIndex = currentTabs.indexOf(selectedTab);
      if (domainIndex >= 0 && domainIndex < activitiesDef.domains.length) {
        return renderDomainContent(activitiesDef.domains[domainIndex]);
      }
      return renderDomainContent(activitiesDef.domains[0]);
    }
    switch (selectedTab.key) {
      case 'Communication':   return renderPlaceholderContent(selectedTab);
      case 'MotorSkills':     return renderPlaceholderContent(selectedTab);
      case 'Cognitive':       return renderPlaceholderContent(selectedTab);
      case 'SocialEmotional': return renderPlaceholderContent(selectedTab);
      default:                return renderPlaceholderContent(selectedTab);
    }
  };

  return (
    <View style={[styles.backgroundContainer, { backgroundColor: appConfig.getAppBackgroundColor() }]}>
      {/* Fixed Header with Navigation Controls - full width background, constrained content */}
      <View style={styles.fixedDomainHeader}>
        <View style={{ maxWidth: appConfig.getHeaderWidthMax(), alignSelf: 'center', width: '100%' }}>
        {/* Tab name row */}
        <View style={styles.domainTitleRow}>
          <Text style={styles.domainTitle}>
            {selectedTab.icon}  {selectedTab.name} Activities
          </Text>
        </View>

        {/* Navigation Controls Row */}
        <View style={styles.navigationRow}>
          <View style={styles.leftNavigationControls} />

          <View style={styles.centerDescription}>
            <Text style={styles.domainDescription}>
              {activeProfile?.name ? `${activeProfile.name}'s ` : ''}{selectedTab.name} Activities
            </Text>
          </View>

          <View style={styles.rightNavigationControls}>
            <TouchableOpacity
              style={[styles.navButton, { backgroundColor: appConfig.getNavButtonColor() }, selectedTabIndex === 0 && styles.navButtonDisabled]}
              onPress={handlePrevTab}
              disabled={selectedTabIndex === 0}
            >
              <Text style={[styles.navButtonText, { fontSize: 20 }]}>◀</Text>
            </TouchableOpacity>

            <TouchableOpacity
              style={[styles.navButton, { backgroundColor: appConfig.getNavButtonColor() }, selectedTabIndex === currentTabs.length - 1 && styles.navButtonDisabled]}
              onPress={handleNextTab}
              disabled={selectedTabIndex === currentTabs.length - 1}
            >
              <Text style={[styles.navButtonText, { fontSize: 20 }]}>▶</Text>
            </TouchableOpacity>
          </View>
        </View>
        </View>
      </View>

      {/* Tab Menu - full width background, content constrained to headerWidthMax */}
      <View style={styles.tabSectionOuter}>
        <ScrollView
          horizontal
          showsHorizontalScrollIndicator={false}
          style={[styles.tabContainer, { maxWidth: appConfig.getHeaderWidthMax(), alignSelf: 'center', width: '100%' }]}
          contentContainerStyle={styles.tabContent}
        >
        {currentTabs.map((tab, index) => (
          <TouchableOpacity
            key={tab.key}
            style={[
              styles.tab,
              selectedTabIndex === index && { backgroundColor: appConfig.getNavButtonColor() }
            ]}
            onPress={() => setSelectedTabIndex(index)}
          >
            <View style={{ flexDirection: 'row', alignItems: 'center', gap: 5 }}>
              <Text style={styles.tabIcon}>{tab.icon}</Text>
              <Text style={[
                styles.tabText,
                selectedTabIndex === index && styles.selectedTabText
              ]}>
                {tab.name}
              </Text>
            </View>
          </TouchableOpacity>
        ))}
        </ScrollView>
      </View>

      {/* Main Content Card */}
      <View style={styles.cardWrapper}>
        <View style={[styles.container, { maxWidth: appConfig.getSingleCardMaxWidth(), width: '100%' }]}>
          <ScrollView style={styles.contentContainer} showsVerticalScrollIndicator={false}>
            {renderContent()}
          </ScrollView>
        </View>
      </View>
    </View>
  );
};

const styles = StyleSheet.create({
  backgroundContainer: {
    flex: 1,
    backgroundColor: '#3b82f6',
  },
  cardWrapper: {
    flex: 1,
    paddingHorizontal: appConfig.getCardMargin(),
    paddingTop: 12,
    alignItems: 'center',
  },
  container: {
    flex: 1,
    backgroundColor: '#f8f9fa',
    borderTopLeftRadius: 12,
    borderTopRightRadius: 12,
    elevation: 2,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.1,
    shadowRadius: 4,
  },
  header: {
    backgroundColor: appConfig.getHeaderBackgroundColor(),
    padding: 16,
    paddingBottom: 8,
  },
  title: {
    fontSize: 24,
    fontWeight: 'bold',
    color: '#ffffff',
    textAlign: 'center',
    textShadowColor: 'rgba(0, 0, 0, 0.3)',
    textShadowOffset: { width: 0, height: 2 },
    textShadowRadius: 4,
  },
  tabSectionOuter: {
    backgroundColor: appConfig.getHeaderBackgroundColor(),
  },
  tabContainer: {
    maxHeight: 60,
  },
  tabContent: {
    paddingHorizontal: 16,
    paddingVertical: 8,
    flexGrow: 1,
    justifyContent: 'center',
  },
  tab: {
    paddingHorizontal: 20,
    paddingVertical: 12,
    marginRight: 8,
    borderRadius: 20,
    backgroundColor: '#f1f3f4',
    minWidth: 100,
    alignItems: 'center',
  },
  tabText: {
    fontSize: 13,
    fontWeight: '600',
    color: '#5f6368',
  },
  tabIcon: {
    fontSize: 15,
  },
  selectedTabText: {
    color: '#ffffff',
  },
  fixedDomainHeader: {
    backgroundColor: appConfig.getHeaderBackgroundColor(),
    paddingTop: 16 + appConfig.getMobileHeaderTopPadding(),
    paddingBottom: 16,
    paddingHorizontal: 24,
  },
  domainTitleRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: 8,
  },
  domainTitle: {
    fontSize: 22,
    fontWeight: 'bold',
    color: '#ffffff',
    flex: 1,
    marginBottom: 4,
    textShadowColor: 'rgba(0, 0, 0, 0.3)',
    textShadowOffset: { width: 0, height: 2 },
    textShadowRadius: 4,
  },
  navigationRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  leftNavigationControls: {
    flexDirection: 'row',
    gap: 8,
    minWidth: 36,
  },
  rightNavigationControls: {
    flexDirection: 'row',
    gap: 8,
  },
  centerDescription: {
    flex: 1,
    alignItems: 'center',
    justifyContent: 'center',
    paddingHorizontal: 8,
  },
  domainDescription: {
    fontSize: 13,
    color: '#e2e8f0',
    textAlign: 'center',
    textShadowColor: 'rgba(0, 0, 0, 0.3)',
    textShadowOffset: { width: 0, height: 1 },
    textShadowRadius: 2,
  },
  domainDescriptionCard: {
    fontSize: 14,
    color: '#ffffff',
    textAlign: 'center',
    lineHeight: 20,
  },
  navButton: {
    width: 36,
    height: 36,
    borderRadius: 18,
    backgroundColor: '#3498db',
    justifyContent: 'center',
    alignItems: 'center',
    elevation: 2,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 1 },
    shadowOpacity: 0.2,
    shadowRadius: 2,
  },
  navButtonDisabled: {
    backgroundColor: '#bdc3c7',
    opacity: 0.6,
  },
  navButtonText: {
    color: '#ffffff',
    fontSize: 16,
    fontWeight: 'bold',
  },
  contentContainer: {
    flex: 1,
  },
  categoryTabs: {
    backgroundColor: '#ffffff',
    paddingVertical: 12,
    paddingHorizontal: 16,
    borderBottomWidth: 1,
    borderBottomColor: '#e1e8ed',
  },
  categoryTab: {
    flexDirection: 'column',
    alignItems: 'center',
    paddingHorizontal: 16,
    paddingVertical: 8,
    marginRight: 8,
    borderRadius: 12,
    backgroundColor: '#f8f9fa',
    minWidth: 80,
  },
  categoryTabIcon: {
    fontSize: 20,
    marginBottom: 4,
  },
  categoryTabText: {
    fontSize: 12,
    color: '#7f8c8d',
    textAlign: 'center',
    fontWeight: '500',
  },
  activeCategoryTabText: {
    color: '#ffffff',
    fontWeight: '600',
  },
  activitiesGrid: {
    padding: 16,
  },
  activityCard: {
    backgroundColor: '#ffffff',
    borderRadius: 12,
    padding: 16,
    marginBottom: 16,
    elevation: 2,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.1,
    shadowRadius: 4,
  },
  activityHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 12,
  },
  activityIcon: {
    fontSize: 32,
    marginRight: 12,
  },
  activityTitleContainer: {
    flex: 1,
  },
  activityTitle: {
    fontSize: 18,
    fontWeight: '600',
    color: '#2c3e50',
    marginBottom: 2,
  },
  activityAge: {
    fontSize: 14,
    color: '#7f8c8d',
  },
  durationBadge: {
    backgroundColor: '#e8f4fd',
    paddingHorizontal: 8,
    paddingVertical: 4,
    borderRadius: 8,
  },
  durationText: {
    fontSize: 12,
    color: '#3498db',
    fontWeight: '500',
  },
  activityDescription: {
    fontSize: 16,
    color: '#34495e',
    marginBottom: 16,
    lineHeight: 22,
  },
  sectionLabel: {
    fontSize: 16,
    fontWeight: '600',
    color: '#2c3e50',
    marginBottom: 8,
  },
  materialsSection: {
    marginBottom: 16,
  },
  materialsList: {
    paddingLeft: 8,
  },
  materialItem: {
    fontSize: 14,
    color: '#34495e',
    marginBottom: 2,
  },
  instructionsSection: {
    marginBottom: 16,
  },
  instructionItem: {
    fontSize: 14,
    color: '#34495e',
    marginBottom: 4,
    paddingLeft: 8,
    lineHeight: 20,
  },
  benefitsSection: {
    marginBottom: 8,
  },
  benefitsList: {
    paddingLeft: 8,
  },
  benefitItem: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 4,
  },
  benefitIcon: {
    fontSize: 14,
    color: '#27ae60',
    marginRight: 8,
    fontWeight: 'bold',
  },
  benefitText: {
    fontSize: 14,
    color: '#34495e',
    flex: 1,
  },
  emptyContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    paddingVertical: 60,
  },
  emptyText: {
    fontSize: 16,
    color: '#7f8c8d',
    textAlign: 'center',
  },
  footer: {
    paddingVertical: 20,
  },
  footerText: {
    fontSize: 14,
    color: '#7f8c8d',
    textAlign: 'center',
    fontStyle: 'italic',
    lineHeight: 20,
  },
  sourceCredit: {
    fontSize: 12,
    color: '#95a5a6',
    textAlign: 'center',
    marginTop: 8,
    lineHeight: 18,
  },
  groupCard: {
    backgroundColor: '#ffffff',
    borderRadius: 14,
    padding: 14,
    marginBottom: 12,
    elevation: 2,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.08,
    shadowRadius: 4,
    borderLeftWidth: 4,
    borderLeftColor: '#3b82f6',
  },
  groupCardTitle: {
    fontSize: 15,
    fontWeight: '700',
    color: '#1e3a5f',
    marginBottom: 4,
  },
  groupCardDesc: {
    fontSize: 13,
    color: '#64748b',
    marginBottom: 8,
    lineHeight: 18,
  },
  groupItemRow: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    marginTop: 6,
  },
  groupItemBullet: {
    fontSize: 14,
    color: '#3b82f6',
    marginRight: 8,
    marginTop: 1,
  },
  groupItemContent: {
    flex: 1,
  },
  groupItemName: {
    fontSize: 14,
    fontWeight: '600',
    color: '#2c3e50',
    lineHeight: 20,
  },
  groupItemDesc: {
    fontSize: 13,
    color: '#4a5568',
    lineHeight: 18,
    marginTop: 2,
  },
  actViewSelector: {
    flexDirection: 'row',
    justifyContent: 'center',
    gap: 8,
    marginBottom: 12,
  },
  actViewBtn: {
    paddingHorizontal: 18,
    paddingVertical: 6,
    borderRadius: 16,
    backgroundColor: '#e8edf2',
  },
  actViewBtnActive: {},  
  actViewBtnText: {
    fontSize: 13,
    fontWeight: '600',
    color: '#5f6368',
  },
  actViewBtnTextActive: {
    color: '#ffffff',
  },
});
